import { User } from 'firebase/auth';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, VStack, HStack, Icon, Text, SimpleGrid, Spinner, Flex } from '@chakra-ui/react';

import Content from '~/containers/Content';
import PageContainer from '~/containers/PageContainer';
import BackwardBar from '~/components/BackwardBar';
import RecordContainer from './RecordContainer';

import platformAtom from '~/recoil/atom/platform';
import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue } from 'recoil';

import { IoTimerOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import * as fp from 'lodash/fp';

import { getContainersRecord } from '~/restApi/container';

import { PLATFORM_ID } from '~/globalVariables';

import { ContainerRecord } from '~/types/container';
import { ContainerOwner } from '~/types/common';

import { Container } from '~/types/platform/platform';
import { FetchingDataStatus } from '~/types/utilities';

export default function ReturnRecord() {
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);
  const platform = useRecoilValue(platformAtom);
  const auth = useRecoilValue(authAtom);

  const navigate = useNavigate();

  const [openOrderId, setOpenOrderId] = useState('');

  const [containerRecords, setContainerRecords] = useState<Array<ContainerRecord>>([]);
  const [containerRecordsStatus, setContainerRecordsStatus] = useState<FetchingDataStatus>(
    FetchingDataStatus.INIT,
  );

  const handleGetContainersRecord = useCallback(async (auth: User) => {
    try {
      setContainerRecordsStatus(FetchingDataStatus.LOADING);
      const res = await getContainersRecord(auth, PLATFORM_ID, ContainerOwner.CUSTOMER);
      setContainerRecordsStatus(FetchingDataStatus.SUCCESS);

      setContainerRecords(res.results || []);
    } catch (error) {
      console.log('get container record error', error);
      setContainerRecordsStatus(FetchingDataStatus.FAIL);
      setContainerRecords([]);
    }
  }, []);

  useEffect(() => {
    if (auth) {
      handleGetContainersRecord(auth);
    }
  }, [auth, handleGetContainersRecord]);

  const handleChangeOrderId = (id: string) => {
    setOpenOrderId((prev) => (fp.eq(prev, id) ? '' : id));
  };

  const transformContainerRecordsList = useMemo(() => {
    const platformContainerMap = platform.containers.reduce<Map<string, Container>>(
      (acc, container) => {
        acc.set(container.id, container);
        return acc;
      },
      new Map(),
    );

    return containerRecords.map(({ id, createdAt, details }) => {
      return {
        id,
        createdAt,
        details: details.map(({ id, count }) => ({
          id,
          count,
          name: platformContainerMap.get(id)?.name || id,
        })),
      };
    });
  }, [containerRecords, platform.containers]);

  return (
    <PageContainer
      hideHeader
      hideNavBar
      afterHeaderComponent={
        <>
          <BackwardBar title="返回" onNavigate={() => navigate(`/mealBoxRecycling`)} />
          <Box bg="white" px={4} borderBottomWidth="1px" borderBottomColor="primary">
            <Content m="auto">
              <HStack py={2}>
                <Icon as={IoTimerOutline} boxSize="1.25rem" color="primary" />
                <Text fontSize="lg" letterSpacing={2}>
                  餐盒歸還紀錄
                </Text>
              </HStack>
            </Content>
          </Box>
        </>
      }
    >
      <Box minH="70vh" bg="white" px={4} py={6}>
        <Content>
          {fp.cond([
            [
              fp.eq(FetchingDataStatus.LOADING),
              fp.always(
                <Flex justify="center">
                  <Spinner />
                </Flex>,
              ),
            ],
            [
              fp.eq(FetchingDataStatus.SUCCESS),
              fp.always(
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  {isLargerThan768 ? (
                    <>
                      <VStack align="stretch" spacing={4}>
                        {transformContainerRecordsList
                          .filter((_, index) => fp.eq(index % 2, 0))
                          .map(({ id, createdAt, details }) => (
                            <RecordContainer
                              key={id}
                              isOpen={fp.eq(id, openOrderId)}
                              id={id}
                              time={createdAt}
                              containerList={details}
                              onChangeOpenOrder={(id) =>
                                setOpenOrderId((prev) => (fp.eq(id, prev) ? '' : id))
                              }
                            />
                          ))}
                      </VStack>
                      <VStack align="stretch" spacing={4}>
                        {transformContainerRecordsList
                          .filter((_, index) => fp.eq(index % 2, 1))
                          .map(({ id, createdAt, details }) => (
                            <RecordContainer
                              key={id}
                              isOpen={fp.eq(id, openOrderId)}
                              id={id}
                              time={createdAt}
                              containerList={details}
                              onChangeOpenOrder={(id) =>
                                setOpenOrderId((prev) => (fp.eq(id, prev) ? '' : id))
                              }
                            />
                          ))}
                      </VStack>
                    </>
                  ) : (
                    <>
                      {transformContainerRecordsList.map(({ id, createdAt, details }) => (
                        <RecordContainer
                          key={id}
                          isOpen={fp.eq(id, openOrderId)}
                          id={id}
                          time={createdAt}
                          containerList={details}
                          onChangeOpenOrder={(id) =>
                            setOpenOrderId((prev) => (fp.eq(id, prev) ? '' : id))
                          }
                        />
                      ))}
                    </>
                  )}
                </SimpleGrid>,
              ),
            ],
            [fp.T, fp.always(<></>)],
          ])(containerRecordsStatus)}
        </Content>
      </Box>
    </PageContainer>
  );
}
